// src/services/accountLinking.ts
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  linkWithPopup,
  sendEmailVerification,
  type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

export type RoddoLinkProvider = 'google' | 'apple';

export type RoddoEmailPasswordLinkResult = RoddoAccountIdentity & {
  verificationSent: boolean;
};

export type RoddoAccountIdentity = {
  uid: string | null;
  isGuest: boolean;
  providers: string[];
  email: string | null;
};

function providerIds(user: User | null): string[] {
  return Array.from(
    new Set((user?.providerData || []).map((row) => row.providerId).filter(Boolean)),
  );
}

export function getRoddoAccountIdentity(): RoddoAccountIdentity {
  const user = auth.currentUser;
  const providers = providerIds(user);
  const isGuest = !!user && (user.isAnonymous || (providers.length === 0 && !user.email));

  return {
    uid: user?.uid ?? null,
    isGuest,
    providers,
    email: user?.email ?? null,
  };
}

function friendlyLinkError(error: any, providerLabel = 'sign-in method'): Error {
  const code = String(error?.code || '');
  if (code.includes('credential-already-in-use') || code.includes('email-already-in-use')) {
    return new Error(`That ${providerLabel} is already attached to another Roddo account.`);
  }
  if (code.includes('provider-already-linked')) {
    return new Error('That sign-in method is already linked to this Roddo account.');
  }
  if (code.includes('popup-closed-by-user')) {
    return new Error('Sign-in was closed before linking finished.');
  }
  if (code.includes('invalid-email')) {
    return new Error('Enter a valid email address.');
  }
  if (code.includes('weak-password')) {
    return new Error('Password is too weak. Use at least 6 characters.');
  }
  if (code.includes('requires-recent-login')) {
    return new Error('For security, sign out and sign back in before adding this sign-in method.');
  }
  if (code.includes('network-request-failed')) {
    return new Error('Network error while linking. Check your connection and try again.');
  }
  return new Error(error?.message || 'Could not link this sign-in method.');
}

export async function linkRoddoEmailPassword(
  email: string,
  password: string,
): Promise<RoddoEmailPasswordLinkResult> {
  const current = auth.currentUser;
  if (!current) throw new Error('Sign in to Roddo before linking an account.');

  const cleanEmail = String(email || '').trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Enter a valid email address.');
  }
  if (String(password || '').length < 6) {
    throw new Error('Password is too weak. Use at least 6 characters.');
  }

  try {
    if (providerIds(current).includes('password')) {
      throw new Error('Email + password is already linked to this Roddo account.');
    }

    const credential = EmailAuthProvider.credential(cleanEmail, password);
    const linked = await linkWithCredential(current, credential);

    await linked.user.getIdToken(true).catch(() => {});
    await linked.user.reload().catch(() => {});

    let verificationSent = false;
    const refreshed = auth.currentUser || linked.user;
    if (refreshed && refreshed.email && refreshed.emailVerified !== true) {
      try {
        await sendEmailVerification(refreshed);
        verificationSent = true;
      } catch (verificationError) {
        // Credential linking is authoritative. Verification delivery is a
        // follow-up and must not make a successfully secured account look failed.
        console.warn('[accountLinking] email linked; verification email deferred', verificationError);
      }
    }

    const identity = getRoddoAccountIdentity();
    return {
      ...identity,
      isGuest: false,
      providers: Array.from(new Set([...identity.providers, 'password'])),
      email: identity.email || cleanEmail,
      verificationSent,
    };
  } catch (error: any) {
    if (error instanceof Error && !String((error as any)?.code || '')) {
      throw error;
    }
    throw friendlyLinkError(error, 'email address');
  }
}

export async function linkRoddoAccountProvider(
  provider: RoddoLinkProvider,
): Promise<RoddoAccountIdentity> {
  const current = auth.currentUser;
  if (!current) throw new Error('Sign in to Roddo before linking an account.');

  try {
    if (Capacitor.isNativePlatform()) {
      const nativeApi: any = FirebaseAuthentication as any;
      const nativeCurrent = await nativeApi.getCurrentUser();
      const nativeUid = nativeCurrent?.user?.uid ?? null;

      if (!nativeUid || nativeUid !== current.uid) {
        throw new Error('Guest account sync is not ready yet. Close and reopen Roddo, then try again.');
      }

      if (provider === 'google') {
        if (typeof nativeApi.linkWithGoogle !== 'function') {
          throw new Error('Google account linking is unavailable in this build.');
        }
        await nativeApi.linkWithGoogle();
      } else {
        if (Capacitor.getPlatform() !== 'ios') {
          throw new Error('Apple account linking is available on iPhone/iPad.');
        }
        if (typeof nativeApi.linkWithApple !== 'function') {
          throw new Error('Apple account linking is unavailable in this build.');
        }
        await nativeApi.linkWithApple();
      }

      await current.getIdToken(true).catch(() => {});
      await current.reload().catch(() => {});
      const expectedProvider = provider === 'google' ? 'google.com' : 'apple.com';
      const identity = getRoddoAccountIdentity();
      return {
        ...identity,
        isGuest: false,
        providers: Array.from(new Set([...identity.providers, expectedProvider])),
      };
    }

    if (provider === 'google') {
      const google = new GoogleAuthProvider();
      google.setCustomParameters({ prompt: 'select_account' });
      await linkWithPopup(current, google);
    } else {
      const apple = new OAuthProvider('apple.com');
      apple.addScope('email');
      apple.addScope('name');
      await linkWithPopup(current, apple);
    }

    await current.reload();
    return getRoddoAccountIdentity();
  } catch (error: any) {
    throw friendlyLinkError(error, provider === 'google' ? 'Google account' : 'Apple account');
  }
}
